import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Button, useToast } from '@chakra-ui/core';
import isEqual from 'lodash/isEqual';
import { AnalyticsPage, ParameterItem, PaneHandle, PaneProps } from '../accountParameter.types';
import { GenericSettingsPane, GenericSettingsPaneHandle } from './GenericSettingsPane';
import { AnalyticsPagesManager } from './AnalyticsPagesManager';
import { useStoreState } from '../../../../models/hooks';

interface AnalyticsState {
  rows: ParameterItem[];
  pages: AnalyticsPage[];
}

export const AnalyticsPane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
  const { t } = useTranslation('accountParameter');
  const toast = useToast();
  const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);
  const account = useStoreState((state) => state.app.accounts);

  const initialAnalyticsItemsConstant: ParameterItem[] = useMemo(
    () => [
      {
        key: 'analyticsEnabled',
        label: t('analyticsPane.analytics'),
        description: t('analyticsPane.analyticsDescription'),
        value: account?.analyticsEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'dashboardEnabled',
        label: t('analyticsPane.todoList'),
        description: t('analyticsPane.todoListDescription'),
        value: account?.configuration?.dashboardEnabled ?? false,
        type: 'boolean' as const,
      },
      {
        key: 'managementOverviewEnabled',
        label: t('analyticsPane.managementOverview'),
        description: t('analyticsPane.managementOverviewDescription'),
        value: account?.configuration?.managementOverviewEnabled ?? false,
        type: 'boolean' as const,
      },
    ],
    [account, t],
  );

  const initialAnalyticsState = useMemo(
    () => ({
      rows: JSON.parse(JSON.stringify(initialAnalyticsItemsConstant)),
      pages: account?.analyticsPages ?? [],
    }),
    [account, initialAnalyticsItemsConstant],
  );

  const [initialState, setInitialState] = useState<AnalyticsState>(initialAnalyticsState);
  const [analyticsRows, setAnalyticsRows] = useState<ParameterItem[]>(initialState.rows);
  const [analyticsPages, setAnalyticsPages] = useState<AnalyticsPage[]>(initialState.pages);

  const [genericPaneDirty, setGenericPaneDirty] = useState(false);
  const pagesDirty = !isEqual(analyticsPages, initialState.pages);

  const isDirty = genericPaneDirty || pagesDirty;

  useEffect(() => {
    if (!isDirty) {
      const newInitialState = {
        rows: JSON.parse(JSON.stringify(initialAnalyticsState.rows)),
        pages: JSON.parse(JSON.stringify(initialAnalyticsState.pages)),
      };
      setInitialState(newInitialState);
      setAnalyticsRows(newInitialState.rows);
      setAnalyticsPages(newInitialState.pages);
    }
  }, [initialAnalyticsState, isDirty]);

  useEffect(() => {
    setDirty(genericPaneDirty || pagesDirty);
  }, [genericPaneDirty, pagesDirty, setDirty]);

  const handleSaveAll = async () => {
    const updates: Record<string, any> = {};
    analyticsRows.forEach((row) => {
      const initialItem = initialState.rows.find((item) => item.key === row.key);
      if (initialItem && !isEqual(row.value, initialItem.value)) {
        updates[row.key] = row.value;
      }
    });

    if (pagesDirty) {
      updates.analyticsPages = analyticsPages;
    }

    if (Object.keys(updates).length > 0) {
      const success = await onSave(updates);
      if (success) {
        setInitialState({
          rows: JSON.parse(JSON.stringify(analyticsRows)),
          pages: JSON.parse(JSON.stringify(analyticsPages)),
        });
        genericPaneRef.current?.resetDirtyState();
      }
    } else {
      toast({ status: 'info', title: t('analyticsPane.noChangesToSave'), isClosable: true });
    }
  };

  const handleResetAll = () => {
    setAnalyticsRows(initialState.rows);
    setAnalyticsPages(initialState.pages);
    genericPaneRef.current?.reset();
  };

  useImperativeHandle(ref, () => ({
    save: handleSaveAll,
    reset: handleResetAll,
  }));

  return (
    <Box>
      {showActions && isDirty && (
        <Flex justify="flex-end" my={4}>
          <Button variant="ghost" mr={3} onClick={handleResetAll}>
            {t('genericSettingsPane.reset')}
          </Button>
          <Button colorScheme="blue" onClick={handleSaveAll}>
            {t('genericSettingsPane.save')}
          </Button>
        </Flex>
      )}
      <GenericSettingsPane
        ref={genericPaneRef}
        setDirty={setGenericPaneDirty}
        showInlineActions={false}
        items={initialState.rows}
        onRowsChange={setAnalyticsRows}
      />
      {/* in AppyHere Configuration */}
      <AnalyticsPagesManager value={analyticsPages} onChange={setAnalyticsPages} baselinePages={initialState.pages} />
    </Box>
  );
});
